import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAttribution } from '../utils/attribution';
export const BookingForm = () => {
  const attribution = useAttribution();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    treatment: '',
    day: '',
    transactionalOptIn: false,
    marketingOptIn: false
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      contact_source: 'Reset General Landing page',
      preferred_location: 'San Ramon, CA',
      treatment_interest: formData.treatment,
      preferred_day: formData.day,
      transactional_sms_opt_in: formData.transactionalOptIn,
      marketing_sms_opt_in: formData.marketingOptIn,
      ...attribution,
      lead_created_timestamp: new Date().toISOString(),
      consent_status: `transactional:${formData.transactionalOptIn}; marketing:${formData.marketingOptIn}`
    };
    const webhookUrl = 'https://services.leadconnectorhq.com/hooks/DBw1tkqBTZflFworLNDq/webhook-trigger/99a04f8d-93f8-4b99-b3b2-11f91e4dcd78';
    try {
      let response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        // Fallback to no-cors if standard fetch fails
        await fetch(webhookUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }
      setStatus('success');
    } catch (error) {
      console.error('Webhook submission error:', error);
      // Try one last time with no-cors
      try {
        await fetch(webhookUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        setStatus('success');
      } catch (fallbackError) {
        setStatus('error');
      }
    }
  };
  if (status === 'success') {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="bg-white rounded-2xl p-8 sm:p-12 text-center shadow-2xl max-w-2xl mx-auto" data-id="element-32">
        <CheckCircle2 className="w-20 h-20 text-be-pink mx-auto mb-6" data-id="element-33" />
        <h3 className="text-be-navy text-3xl font-display tracking-wide mb-4" data-id="element-34">
          You're in!
        </h3>
        <p className="text-be-navy-light text-lg" data-id="element-35">
          Check your phone — we'll text you within one business day to lock in
          your consult.
        </p>
      </motion.div>;
  }
  return <section id="booking-form" className="w-full bg-be-navy py-20 px-4 relative" data-id="element-36">
      <div className="max-w-3xl mx-auto" data-id="element-37">
        <div className="text-center mb-10" data-id="element-38">
          <div className="h-px bg-be-pink w-10 mx-auto mb-5" data-id="element-39" />
          <p className="text-be-pink text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase mb-4" data-id="element-40">
            Claim Your Credit
          </p>
          <h2 className="text-white font-black uppercase tracking-tight text-3xl sm:text-4xl md:text-5xl mb-2" data-id="element-41">
            Your{' '}
            <span className="font-serif italic font-normal text-be-pink-light" data-id="element-42">
              $150
            </span>{' '}
            Awaits<span className="text-be-pink" data-id="element-43">.</span>
          </h2>
          <p className="text-white/70 text-base sm:text-lg max-w-xl mx-auto leading-relaxed mt-4" data-id="element-44">
            Tell us where to send your consult details — we'll text you to
            confirm your time.
          </p>
        </div>

        <div className="bg-[#FFFDFD] rounded-2xl p-6 sm:p-10 shadow-2xl text-be-navy" data-id="element-45">
          {status === 'error' && <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 flex items-start gap-3" role="alert" data-id="element-46">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-47" />
              <p className="text-sm font-medium" data-id="element-48">
                Something went wrong submitting your request. Please try again.
              </p>
            </div>}

          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-49">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-50">
              <div data-id="element-51">
                <label htmlFor="firstName" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-52">
                  First Name *
                </label>
                <input required type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-be-pink focus:border-be-pink outline-none transition-all bg-white" data-id="element-53" />
              </div>
              <div data-id="element-54">
                <label htmlFor="lastName" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-55">
                  Last Name *
                </label>
                <input required type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-be-pink focus:border-be-pink outline-none transition-all bg-white" data-id="element-56" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-57">
              <div data-id="element-58">
                <label htmlFor="email" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-59">
                  Email *
                </label>
                <input required type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-be-pink focus:border-be-pink outline-none transition-all bg-white" data-id="element-60" />
              </div>
              <div data-id="element-61">
                <label htmlFor="phone" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-62">
                  Phone *
                </label>
                <input required type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-be-pink focus:border-be-pink outline-none transition-all bg-white" data-id="element-63" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-64">
              <div data-id="element-65">
                <label htmlFor="treatment" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-66">
                  Treatment of Interest
                </label>
                <select id="treatment" name="treatment" value={formData.treatment} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-be-pink focus:border-be-pink outline-none transition-all bg-white appearance-none text-sm" data-id="element-67">
                  <option value="" disabled data-id="element-68">
                    Select (Optional)
                  </option>
                  <option value="Not sure yet — help me decide" data-id="element-69">
                    Not sure yet — help me decide
                  </option>
                  <option value="Neuromodulators (Botox / Dysport / Xeomin)" data-id="element-70">
                    Neuromodulators (Botox / Dysport / Xeomin)
                  </option>
                  <option value="Dermal Fillers / Facial Balancing" data-id="element-71">
                    Dermal Fillers / Facial Balancing
                  </option>
                  <option value="Lip Filler / Lip Enhancement" data-id="element-72">
                    Lip Filler / Lip Enhancement
                  </option>
                  <option value="Kybella (Double Chin Reduction)" data-id="element-73">
                    Kybella (Double Chin Reduction)
                  </option>
                  <option value="Sculptra (Collagen Biostimulator)" data-id="element-74">
                    Sculptra (Collagen Biostimulator)
                  </option>
                  <option value="PRP / PRF (Skin & Hair)" data-id="element-75">
                    PRP / PRF (Skin & Hair)
                  </option>
                  <option value="Microneedling with PRP" data-id="element-76">
                    Microneedling with PRP
                  </option>
                  <option value="Chemical Peel" data-id="element-77">Chemical Peel</option>
                  <option value="HydraFacial" data-id="element-78">HydraFacial</option>
                  <option value="IPL Photofacial" data-id="element-79">IPL Photofacial</option>
                  <option value="Clear + Brilliant Laser" data-id="element-80">
                    Clear + Brilliant Laser
                  </option>
                  <option value="Morpheus8 RF Microneedling" data-id="element-81">
                    Morpheus8 RF Microneedling
                  </option>
                  <option value="Laser Hair Removal" data-id="element-82">Laser Hair Removal</option>
                  <option value="CoolSculpting / Body Contouring" data-id="element-83">
                    CoolSculpting / Body Contouring
                  </option>
                  <option value="Medical-Grade Skincare Consult" data-id="element-84">
                    Medical-Grade Skincare Consult
                  </option>
                  <option value="Other" data-id="element-85">Other</option>
                </select>
              </div>
              <div data-id="element-86">
                <label htmlFor="day" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-87">
                  Preferred Day
                </label>
                <select id="day" name="day" value={formData.day} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-be-pink focus:border-be-pink outline-none transition-all bg-white appearance-none" data-id="element-88">
                  <option value="" disabled data-id="element-89">
                    Select (Optional)
                  </option>
                  <option value="This week" data-id="element-90">This week</option>
                  <option value="Next week" data-id="element-91">Next week</option>
                  <option value="Flexible" data-id="element-92">Flexible</option>
                </select>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-gray-200" data-id="element-93">
              <label className="flex items-start gap-3 cursor-pointer group" data-id="element-94">
                <div className="relative flex items-start pt-1" data-id="element-95">
                  <input required type="checkbox" name="transactionalOptIn" checked={formData.transactionalOptIn} onChange={handleChange} className="w-5 h-5 rounded border-gray-300 text-be-pink focus:ring-be-pink cursor-pointer" data-id="element-96" />
                </div>
                <span className="text-xs text-gray-600 leading-relaxed" data-id="element-97">
                  I agree to receive transactional SMS from Be Clinical for
                  appointment confirmations, reminders, and account-related
                  messages. Message & data rates may apply. Reply STOP to opt
                  out. *
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group" data-id="element-98">
                <div className="relative flex items-start pt-1" data-id="element-99">
                  <input type="checkbox" name="marketingOptIn" checked={formData.marketingOptIn} onChange={handleChange} className="w-5 h-5 rounded border-gray-300 text-be-pink focus:ring-be-pink cursor-pointer" data-id="element-100" />
                </div>
                <span className="text-xs text-gray-600 leading-relaxed" data-id="element-101">
                  I'd like to receive marketing SMS from Be Clinical about
                  offers, events, and new treatments. Message & data rates may
                  apply. Reply STOP to opt out.
                </span>
              </label>
            </div>

            <div className="pt-6" data-id="element-102">
              <button type="submit" disabled={status === 'submitting'} className="w-full bg-be-pink hover:bg-be-pink-light text-white rounded-full py-4 px-8 font-bold tracking-[0.1em] uppercase transition-colors flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-be-pink/30" data-id="element-103">
                {status === 'submitting' ? <>
                    <Loader2 className="w-5 h-5 animate-spin" data-id="element-104" /> SENDING...
                  </> : 'CLAIM MY $150 CREDIT'}
              </button>
              <p className="text-center text-[10px] text-gray-500 mt-4 uppercase tracking-wider" data-id="element-105">
                By submitting, you agree to our Privacy Policy and Terms.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>;
};